import type {
  IFolderDto,
  IGenericPageDto,
  IErrorResponse,
  INavigationNodeDto,
  IHomePageDto,
} from "@/types/filteredContentTypes";
import type { ILanguage } from "@/types/languageTypes";
import type { IWhatsNewResponseDto } from "@/types/whatsNewTypes";
import { appConfigService } from "@/services/appConfigService";
import { authService } from "@/services/authService";
import type { IExportResult } from "@/types/apiResponseTypes";
import type { IUserDto } from "@/types/userTypes";
import { normalizeLocaleForCulture } from "@/utils/localeRouteHelpers";

/**
 * API Service
 * Handles all API calls to the Umbraco backend
 */

async function getApiBaseUrl(): Promise<string> {
  const config = await appConfigService.getAppEnvironmentData();
  return config.apiBaseUrl;
}

/**
 * Creates headers with authentication token
 */
async function getAuthHeaders(options?: { forceRefresh?: boolean }): Promise<HeadersInit> {
  // Ensure MSAL is initialized before attempting to get token
  await authService.createAuthProvider();

  const token = await authService.getAccessToken(options?.forceRefresh ?? false);

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else {
    console.error("API request: No access token available - request will be unauthenticated!");
  }

  return headers;
}

async function fetchWithAuthRetry(url: string, init: RequestInit): Promise<Response> {
  const response = await fetch(url, init);

  // Retry once with a forced token refresh when auth has likely gone stale.
  if (response.status !== 401 && response.status !== 403) {
    return response;
  }

  const refreshedHeaders = await getAuthHeaders({ forceRefresh: true });
  const retriedResponse = await fetch(url, {
    ...init,
    headers: {
      ...init.headers,
      ...refreshedHeaders,
    },
  });

  return retriedResponse;
}

/**
 * Get all nodes (generic pages with their folder children) from the API with optional culture
 */
async function getNodes(culture?: string): Promise<INavigationNodeDto[]> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    // Build query string with culture parameter if provided
    const queryParams = new URLSearchParams();
    const normalizedCulture = normalizeLocaleForCulture(culture) ?? culture;
    if (normalizedCulture) {
      queryParams.append("culture", normalizedCulture);
    }
    const queryString = queryParams.toString();
    const url = queryString
      ? `${baseUrl}/filtered-content/nodes?${queryString}`
      : `${baseUrl}/filtered-content/nodes`;

    const response = await fetchWithAuthRetry(url, {
      method: "GET",
      headers,
      credentials: "include", // Include credentials for CORS
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch nodes: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching nodes:", error);
    throw error;
  }
}

/**
 * Get children of a navigation node (lazy loading)
 */
async function getNodeChildren(nodeId: string, culture?: string): Promise<INavigationNodeDto[]> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const queryParams = new URLSearchParams();
    const normalizedCulture = normalizeLocaleForCulture(culture) ?? culture;
    if (normalizedCulture) {
      queryParams.append("culture", normalizedCulture);
    }
    const queryString = queryParams.toString();
    const url = queryString
      ? `${baseUrl}/filtered-content/nodes/${nodeId}/children?${queryString}`
      : `${baseUrl}/filtered-content/nodes/${nodeId}/children`;

    const response = await fetchWithAuthRetry(url, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch node children: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching node children:", error);
    throw error;
  }
}

/**
 * Search navigation nodes by title
 */
async function searchNodes(
  query: string,
  culture?: string,
  signal?: AbortSignal
): Promise<INavigationNodeDto[]> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const queryParams = new URLSearchParams();
    queryParams.append("query", query);
    const normalizedCulture = normalizeLocaleForCulture(culture) ?? culture;
    if (normalizedCulture) {
      queryParams.append("culture", normalizedCulture);
    }

    const response = await fetchWithAuthRetry(
      `${baseUrl}/filtered-content/nodes/search?${queryParams.toString()}`,
      {
        method: "GET",
        headers,
        credentials: "include",
        signal,
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to search nodes: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (
      (error instanceof DOMException && error.name === "AbortError") ||
      (typeof error === "object" &&
        error !== null &&
        "name" in error &&
        (error as { name?: unknown }).name === "AbortError")
    ) {
      throw error;
    }

    console.error("Error searching nodes:", error);
    throw error;
  }
}

/**
 * Get a specific folder and its children by ID
 */
async function getFolderById(id: string, culture?: string): Promise<IFolderDto> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const queryParams = new URLSearchParams();
    const normalizedCulture = normalizeLocaleForCulture(culture) ?? culture;
    if (normalizedCulture) {
      queryParams.append("culture", normalizedCulture);
    }
    const queryString = queryParams.toString();
    const url = queryString
      ? `${baseUrl}/filtered-content/folder/${id}?${queryString}`
      : `${baseUrl}/filtered-content/folder/${id}`;

    const response = await fetchWithAuthRetry(url, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch folder: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching folder:", error);
    throw error;
  }
}

/**
 * Get page by URL with optional culture parameter
 */
async function getPageByUrl(
  url: string,
  culture?: string
): Promise<IGenericPageDto | IErrorResponse | null> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    // Avoid double-encoding paths (e.g. '/ja-JP/%E3%82...' becoming '%25E3%2582...').
    // The backend expects a normal URL path; URLSearchParams will handle encoding.
    let normalizedUrl = url;
    try {
      if (/%[0-9A-Fa-f]{2}/.test(normalizedUrl)) {
        normalizedUrl = decodeURI(normalizedUrl);
      }
    } catch {
      // If decoding fails due to malformed encoding, fall back to the original input.
      normalizedUrl = url;
    }

    // Build query string with culture parameter if provided
    const queryParams = new URLSearchParams({ url: normalizedUrl });
    const normalizedCulture = normalizeLocaleForCulture(culture) ?? culture;
    if (normalizedCulture) {
      queryParams.append("culture", normalizedCulture);
    }

    const response = await fetchWithAuthRetry(
      `${baseUrl}/filtered-content/page-by-url?${queryParams.toString()}`,
      {
        headers,
      }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      // Prefer backend-provided error payloads when available.
      try {
        const errorBody = (await response.json()) as unknown;
        if (
          errorBody &&
          typeof errorBody === "object" &&
          "code" in errorBody &&
          "message" in errorBody
        ) {
          return errorBody as IErrorResponse;
        }
      } catch {
        // Ignore JSON parse errors.
      }

      if (response.status === 401 || response.status === 403) {
        return { code: "UNAUTHORIZED_ACCESS", message: "Unauthorized" };
      }

      return {
        code: "API_ERROR",
        message: `Request failed: ${response.status} ${response.statusText}`,
      };
    }

    return (await response.json()) as IGenericPageDto;
  } catch (error) {
    console.error("Error fetching page by URL:", error);
    return {
      code: "API_FETCH_FAILED",
      message: error instanceof Error ? error.message : "Failed to fetch page",
    };
  }
}

/**
 * Get page by identifier (ID or GUID key) with optional culture parameter.
 * Useful for locale switching where the URL slug differs per culture.
 */
async function getPageById(
  id: string,
  culture?: string
): Promise<IGenericPageDto | IErrorResponse | null> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const normalizedCulture = normalizeLocaleForCulture(culture) ?? culture;
    const queryString = normalizedCulture
      ? `?culture=${encodeURIComponent(normalizedCulture)}`
      : "";
    const response = await fetchWithAuthRetry(
      `${baseUrl}/filtered-content/page/${encodeURIComponent(id)}${queryString}`,
      { headers }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      try {
        const errorBody = (await response.json()) as unknown;
        if (
          errorBody &&
          typeof errorBody === "object" &&
          "code" in errorBody &&
          "message" in errorBody
        ) {
          return errorBody as IErrorResponse;
        }
      } catch {
        // Ignore JSON parse errors.
      }

      if (response.status === 401 || response.status === 403) {
        return { code: "UNAUTHORIZED_ACCESS", message: "Unauthorized" };
      }

      return {
        code: "API_ERROR",
        message: `Request failed: ${response.status} ${response.statusText}`,
      };
    }

    return (await response.json()) as IGenericPageDto;
  } catch (error) {
    console.error("Error fetching page by ID:", error);
    return {
      code: "API_FETCH_FAILED",
      message: error instanceof Error ? error.message : "Failed to fetch page",
    };
  }
}

/**
 * Get node names for all cultures
 */
async function getNodeNames(nodeId: string): Promise<Record<string, string>> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    const response = await fetchWithAuthRetry(`${baseUrl}/filtered-content/node/${nodeId}/names`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch node names: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching node names:", error);
    throw error;
  }
}

/**
 * Get all enabled languages from Umbraco
 */
async function getLanguages(): Promise<ILanguage[]> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    const response = await fetchWithAuthRetry(`${baseUrl}/languages`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch languages: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching languages:", error);
    throw error;
  }
}

/**
 * Check if the current user is an admin
 */
async function isAdmin(): Promise<boolean> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();
    const response = await fetchWithAuthRetry(`${baseUrl}/user/is-admin`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      return false;
    }

    const data: { isAdmin: boolean } = await response.json();
    return data.isAdmin;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
}

/**
 * Extracts filename from Content-Disposition header with fallback
 */
function extractFilename(response: Response, isFullGuide: boolean): string {
  const contentDisposition = response.headers.get("Content-Disposition");
  if (contentDisposition) {
    // First try filename* (RFC 5987 - UTF-8 encoded) - takes precedence
    const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\r\n]*)/i);
    if (filenameStarMatch?.[1]) {
      return decodeURIComponent(filenameStarMatch[1]);
    }

    // Fall back to filename= (ASCII)
    const filenameMatch = contentDisposition.match(/filename="?([^";\r\n]*)"?/i);
    if (filenameMatch?.[1]) {
      return filenameMatch[1];
    }
  }

  // Fallback: generate filename based on context
  const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const suffix = isFullGuide ? "_FullGuide" : "";
  return `export${suffix}_${timestamp}.pdf`;
}

/**
 * Export content as PDF
 * Uses the GET export/{id} endpoint
 */
async function exportContent(
  id: string,
  isFullGuide: boolean = false,
  culture?: string
): Promise<IExportResult> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const queryParams = new URLSearchParams();
    const normalizedCulture = normalizeLocaleForCulture(culture) ?? culture;

    if (normalizedCulture) {
      queryParams.append("culture", normalizedCulture);
    } else {
      queryParams.append("culture", "en-US");
    }

    const url = isFullGuide
      ? `${baseUrl}/export/all?${queryParams.toString()}`
      : `${baseUrl}/export/${id}?${queryParams.toString()}`;

    const response = await fetchWithAuthRetry(url, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to export content: ${response.statusText}`);
    }

    const blob = await response.blob();
    const filename = extractFilename(response, isFullGuide);

    return { blob, filename };
  } catch (error) {
    console.error("Error exporting content:", error);
    throw error;
  }
}

/**
 * Get user details for the current user
 */
async function getUserDetails(): Promise<IUserDto> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const response = await fetchWithAuthRetry(`${baseUrl}/user/details`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      return {
        isPartner: false,
        isMicrosoftUser: false,
      };
    }

    return await response.json();
  } catch {
    return {
      isPartner: false,
      isMicrosoftUser: false,
    };
  }
}

/**
 * Get recent AI-generated summaries for the What's New page.
 */
async function getWhatsNew(
  page: number = 1,
  pageSize: number = 20,
  culture?: string
): Promise<IWhatsNewResponseDto> {
  const baseUrl = await getApiBaseUrl();
  const headers = await getAuthHeaders();

  const queryParams = new URLSearchParams();
  queryParams.append("page", String(page));
  queryParams.append("pageSize", String(pageSize));
  if (culture) {
    queryParams.append("culture", culture);
  }

  const response = await fetchWithAuthRetry(`${baseUrl}/whats-new?${queryParams.toString()}`, {
    method: "GET",
    headers,
    credentials: "include",
  });

  if (!response.ok) {
    let detail = response.statusText;

    try {
      const errorBody = (await response.json()) as { detail?: unknown; title?: unknown };
      if (typeof errorBody.detail === "string" && errorBody.detail.trim()) {
        detail = errorBody.detail;
      } else if (typeof errorBody.title === "string" && errorBody.title.trim()) {
        detail = errorBody.title;
      }
    } catch {
      // Keep the HTTP status text when the response is not JSON.
    }

    throw new Error(`Failed to fetch what's new: ${response.status} ${detail}`);
  }

  return (await response.json()) as IWhatsNewResponseDto;
}

/**
 * Get home page content from the CMS
 */
async function getHomePage(culture?: string): Promise<IHomePageDto | null> {
  try {
    const baseUrl = await getApiBaseUrl();
    const headers = await getAuthHeaders();

    const queryParams = new URLSearchParams();
    if (culture) {
      queryParams.append("culture", culture);
    }
    const queryString = queryParams.toString();
    const url = queryString
      ? `${baseUrl}/filtered-content/home-page?${queryString}`
      : `${baseUrl}/filtered-content/home-page`;

    const response = await fetchWithAuthRetry(url, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch home page: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching home page:", error);
    return null;
  }
}

export const apiService = {
  getNodes,
  getNodeChildren,
  searchNodes,
  getFolderById,
  getPageByUrl,
  getPageById,
  getNodeNames,
  getLanguages,
  isAdmin,
  exportContent,
  getUserDetails,
  getWhatsNew,
  getHomePage,
};
